/** Formatação de dados do painel. Nada de data "naive": tudo passa por fuso. */

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function formatarMoeda(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined) return '—';
  return MOEDA.format(centavos / 100);
}

export function formatarDataHora(iso: string | null | undefined, fuso: string): string {
  if (!iso) return '—';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(data);
}

/** Data civil YYYY-MM-DD sem sofrer deslocamento de fuso. */
export function formatarDataCivil(data: string | null | undefined): string {
  if (!data) return '—';
  const partes = data.slice(0, 10).split('-');
  if (partes.length !== 3) return data;
  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
}

export function mascararDocumento(documento: string): string {
  const digitos = documento.replace(/\D/g, '');
  if (digitos.length === 11) return `***.***.${digitos.slice(6, 9)}-${digitos.slice(9)}`;
  if (digitos.length === 14) return `**.***.***/****-${digitos.slice(12)}`;
  return documento;
}

export function formatarTelefone(telefone: string | null | undefined): string {
  if (!telefone) return '—';
  const digitos = telefone.replace(/\D/g, '');
  const semPais = digitos.startsWith('55') ? digitos.slice(2) : digitos;
  if (semPais.length === 11) {
    return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 7)}-${semPais.slice(7)}`;
  }
  if (semPais.length === 10) {
    return `(${semPais.slice(0, 2)}) ${semPais.slice(2, 6)}-${semPais.slice(6)}`;
  }
  return telefone;
}

export function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

/** Vocabulário visual de status: cor + ícone + texto, sempre juntos (ADR-0007). */
export const STATUS: Record<
  string,
  { rotulo: string; icone: string; variante: 'ok' | 'alerta' | 'erro' | 'info' | 'neutro' }
> = {
  pendente: { rotulo: 'Pendente', icone: '◷', variante: 'alerta' },
  na_fila: { rotulo: 'Na fila', icone: '↗', variante: 'info' },
  enviado: { rotulo: 'Enviado', icone: '✓', variante: 'ok' },
  falhou: { rotulo: 'Falhou', icone: '✕', variante: 'erro' },
  suprimido: { rotulo: 'Suprimido', icone: '⊘', variante: 'neutro' },
  cancelado: { rotulo: 'Cancelado', icone: '—', variante: 'neutro' },
  aguardando: { rotulo: 'Aguardando canal', icone: '◷', variante: 'alerta' },
  entregue: { rotulo: 'Entregue', icone: '✓✓', variante: 'ok' },
  lida: { rotulo: 'Lida', icone: '✓✓', variante: 'ok' },
  falha_envio: { rotulo: 'Falha no envio', icone: '✕', variante: 'erro' },
  sem_whatsapp: { rotulo: 'Sem WhatsApp', icone: '✕', variante: 'erro' },
  bloqueado: { rotulo: 'Bloqueado', icone: '⊘', variante: 'erro' },
};

export const ROTULO_RECEITA: Record<string, string> = {
  lembrete_preventivo: 'Lembrete Preventivo',
  dia_vencimento: 'Dia do Vencimento',
  inadimplencia_leve: 'Inadimplência Leve',
  inadimplencia_critica: 'Inadimplência Crítica',
  aniversario: 'Aniversário',
};

export const MOTIVOS_SUPRESSAO: Record<string, string> = {
  OPT_OUT: 'Titular pediu para não receber',
  SEM_TELEFONE: 'Cadastro sem telefone',
  JANELA_24H_FECHADA: 'Janela de 24h fechada',
  DUPLICADO: 'Já enviado nesta régua',
  TEMPLATE_AUSENTE: 'Template não vinculado',
  RECEITA_INATIVA: 'Receita desligada',
};

/** Dados de exemplo para pré-visualizar a mensagem sem enviar nada. */
export function variaveisExemplo(empresa: string): Record<string, string> {
  return {
    primeiro_nome: 'Ana',
    nome: 'Ana Souza Lima',
    valor: 'R$ 99,90',
    vencimento: '23/11/2026',
    contrato: '847291',
    fatura: '884512',
    empresa,
    dias_desbloqueio: '4',
    data_liberacao: '27/11/2026',
  };
}

export function preencherModelo(corpo: string, variaveis: Record<string, string>): string {
  return corpo.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (original, chave: string) => {
    const valor = variaveis[chave];
    return valor === undefined ? original : valor;
  });
}

export function variaveisDoCorpo(corpo: string): string[] {
  const encontradas = new Set<string>();
  for (const achado of corpo.matchAll(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi)) {
    if (achado[1]) encontradas.add(achado[1]);
  }
  return [...encontradas];
}

/* ───────────────────────── Contraste (WCAG) ───────────────────────── */

function canalLinear(valor: number): number {
  const c = valor / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function luminancia(hex: string): number {
  const limpo = hex.replace('#', '');
  const expandido =
    limpo.length === 3
      ? limpo
          .split('')
          .map((c) => c + c)
          .join('')
      : limpo;
  if (!/^[0-9a-f]{6}$/i.test(expandido)) return 0;
  const r = Number.parseInt(expandido.slice(0, 2), 16);
  const g = Number.parseInt(expandido.slice(2, 4), 16);
  const b = Number.parseInt(expandido.slice(4, 6), 16);
  return 0.2126 * canalLinear(r) + 0.7152 * canalLinear(g) + 0.0722 * canalLinear(b);
}

export function razaoContraste(corA: string, corB: string): number {
  const a = luminancia(corA);
  const b = luminancia(corB);
  const claro = Math.max(a, b);
  const escuro = Math.min(a, b);
  return (claro + 0.05) / (escuro + 0.05);
}

/** AA para texto normal: 4,5:1. */
export function passaAA(corTexto: string, corFundo: string): boolean {
  return razaoContraste(corTexto, corFundo) >= 4.5;
}

export function horaValida(valor: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(valor);
}
